import { useEffect, useRef, useState } from 'react';
import { Card, Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import * as apiUser from 'api/apiUsers';
import { useGlobal } from 'context/GlobalContext';
import { useToast } from 'context/ToastContext';
import ESpinner from 'components/others/Spinner';
import { useListOnPage } from 'utils/helpers/list/useListOnPage';
import TitleCounter from 'components/others/TitleCounter';
import OffUserCard from './OffUserCard';
import UsersTable from './UsersTable';
import UsersToolBar from './UsersToolBar';
import type { UserType } from '@econtractor/types';

const Users = () => {
    const { t } = useTranslation();
    const { g } = useGlobal();
    const { showToast } = useToast();
    const [usersList, setUsersList] = useState<UserType[] | null>(null);
    const [showSpinner, setShowSpinner] = useState(false);
    const location = useLocation();
    const onPage = useListOnPage();
    const prevUserData = useRef<string | null>(null);
    const showSpinnerRef = useRef(showSpinner);
    const [page, setPage] = useState<number | null>(null);
    const [showUserCard, setShowUserCard] = useState<number | false>(false);

    showSpinnerRef.current = showSpinner;

    const fetchUsers = async (force = false) => {
        if (force) prevUserData.current = null;
        if (!page) return null;
        // При force не блокуємо через showSpinner (інакше після збереження картки список не оновлюється)
        if (!force && showSpinnerRef.current) return null;

        const search = `%${g?.search?.[location.pathname] || ''}%`;
        const userData = {
            search,
            page: page < 0 ? 1 : page || 1,
            onPage,
        };

        if (prevUserData.current == JSON.stringify(userData)) return;
        prevUserData.current = JSON.stringify(userData);

        setShowSpinner(true);
        const resUser = await apiUser.getUsers(userData);
        setShowSpinner(false);
        if (!resUser) return showToast(t('common:messError'), 'danger');
        setUsersList(resUser?.users);

        g.setPaginator((prev) => ({
            ...prev,
            [location.pathname]: resUser.paginator,
        }));
    };

    useEffect(() => {
        if (page == 1) {
            fetchUsers(true);
        } else setPage(1);
    }, [g.search?.[location.pathname], g.target]);

    useEffect(() => {
        const _page = g?.paginator?.[location.pathname]?.currentPage || 1;
        if (page != _page) setPage(_page);
    }, [g?.paginator?.[location.pathname]?.currentPage, g?.paginator?.[location.pathname]?.countRows, g?.paginator?.[location.pathname]?.onPage]);

    useEffect(() => {
        fetchUsers();
    }, [page, onPage]);

    return (
        <>
            <OffUserCard
                showUserCard={showUserCard}
                setShowUserCard={setShowUserCard}
                fetchUsers={fetchUsers}
                setShowSpinner={setShowSpinner}
            />
            <Row>
                <Col className="col-sticky" sm={12}>
                    <div className="w-100 d-flex pt-1 justify-content-between align-items-start">
                        <div>
                            <h4 className="p-0 m-0 mb-0 me-2 text-break">{t('Users.title')}</h4>
                            <TitleCounter />
                        </div>
                        <div>
                            <UsersToolBar setShowUserCard={setShowUserCard} />
                        </div>
                    </div>
                </Col>

                <Col sm={12}>
                    <div className="px-0">
                            {usersList ? (
                                <UsersTable
                                    usersList={usersList}
                                    fetchUsers={fetchUsers}
                                    setShowUserCard={setShowUserCard}
                                    showSpinner={showSpinner}
                                    setShowSpinner={setShowSpinner}
                                />
                            ) : (
                                <ESpinner />
                            )}
                        </div>
                </Col>
            </Row>
        </>
    );
};

export default Users;
